import {
  Box,
  DialogActions,
  DialogContent,
  IconButton,
  Input,
} from '@mui/material';
import React from 'react';
import * as Ramda from 'ramda';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircleOutlineRounded } from '@mui/icons-material';
import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import { InputLabelBase } from '../../../../../../../../../components/inputs/InputLabelBase';
import MultiSelectBase from '../../../../../../../../../components/Selects/MultiSelect';
import { transformToOption } from '../../../../../../../../../utils/functional/fp';
import { withFormController } from '../../../../../../../../../components/hocs/forms';
import { ContextTypes } from '../amendatory-coverages/manager';

export const AdditionalNamedInsuredModal = ({
  data: modalProps,
  close,
}: ModalProps) => {
  const [aniOptions, setAniOptions] = React.useState(() =>
    modalProps.options.map(transformToOption())
  );
  const [newAni, setNewAni] = React.useState('');
  const formMethods = useForm({
    defaultValues: {
      ani: modalProps.aniValues ?? [],
    },
    resolver: yupResolver(schema),
  });

  const ani = formMethods.watch('ani');
  const isLargeList = aniOptions.length > 10;

  function addAni() {
    setAniOptions((prev) => [
      ...prev,
      { value: newAni, label: newAni, meta: newAni },
    ]);
    const nextAniValues = [...ani, newAni];
    formMethods.setValue('ani', nextAniValues);

    setNewAni('');
  }

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 3rem', margin: '0 auto' }}>
          <InputLabelBase style={{ padding: '0.25rem' }} required>
            Add/Remove Additional Named Insureds <i>({ani.length} selected)</i>
          </InputLabelBase>
          <MultiSelect
            name="ani"
            options={aniOptions as any}
            isSearchable={isLargeList}
            showSelectAll={isLargeList}
            renderCustomAction={() =>
              modalProps.context === ContextTypes.ENDORSEMENTS ? (
                <Box position="relative" pt="0.15rem">
                  <Input
                    placeholder="Add new ANI"
                    sx={{
                      '& .MuiInput-input': {
                        border: 'none',
                      },
                      ' & .MuiInput-input:focus': {
                        background: (theme) => theme.palette.background.active,
                      },
                      opacity: (theme) =>
                        theme.palette.mode === 'dark' ? 1 : 0.8,
                    }}
                    value={newAni}
                    onChange={(e) => setNewAni(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newAni.trim()) {
                        addAni();
                      }
                    }}
                  />
                  <Box
                    position="absolute"
                    top="50%"
                    right={3}
                    style={{ transform: 'translateY(-50%)' }}
                  >
                    <IconButton onClick={addAni} disabled={!newAni.trim()}>
                      <AddCircleOutlineRounded />
                    </IconButton>
                  </Box>
                </Box>
              ) : null
            }
          />
        </DialogContent>
        <DialogActions>
          <CbButton onClick={close} styleName="cancel">
            Cancel
          </CbButton>
          <CbButton styleName="ctaButton" type="submit">
            Save
          </CbButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export const AdditionalNamedInsuredModalConfig = {
  AdditionalNamedInsuredModal: {
    component: AdditionalNamedInsuredModal,
    config: {
      title: 'Add/Edit Additional Named Insureds',
      fullWidth: true,
      maxWidth: 'sm',
    },
  },
};

const MultiSelect = withFormController(MultiSelectBase);

type ModalProps = {
  data: {
    onSubmit: (formValues: AniFormValues) => void;
    options: string[];
    aniValues: string[];
    context: ContextTypes;
  };
  close: () => void;
};

const schema = Yup.object().shape({
  ani: Yup.array()
    .of(Yup.string())
    .required()
    .label('Additional Named Insureds'),
});

// Yup.InferType collapses this schema to an empty type on the installed Yup
// version, so the shape is declared explicitly to match `schema` above.
export type AniFormValues = { ani: string[] };
